import { decryptAesEcb as decryptAesEcbBytes } from './aes.ts';

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

function stringToBytes(input: string): Uint8Array {
  return new TextEncoder().encode(input);
}

function binaryStringToBytes(input: string): Uint8Array {
  return Uint8Array.from(input, (char) => char.charCodeAt(0) & 0xff);
}

export function hexEncode(input: Uint8Array | number[] | string): string {
  if (typeof input === 'string') return bytesToHex(stringToBytes(input));
  return bytesToHex(input instanceof Uint8Array ? input : Uint8Array.from(input));
}

export function encodeBase64(input: string): string {
  return btoa(input);
}

export function decodeBase64(input: string): string {
  return atob(input);
}

export function decryptAesEcb(input: Uint8Array | number[] | string, key: Uint8Array | number[] | string): Uint8Array {
  const inputBytes =
    typeof input === 'string'
      ? binaryStringToBytes(input)
      : input instanceof Uint8Array
        ? input
        : Uint8Array.from(input);
  const keyBytes =
    typeof key === 'string'
      ? stringToBytes(key)
      : key instanceof Uint8Array
        ? key
        : Uint8Array.from(key);
  return decryptAesEcbBytes(inputBytes, keyBytes);
}

function rotateLeft(value: number, shift: number): number {
  return (value << shift) | (value >>> (32 - shift));
}

function add32(a: number, b: number): number {
  return (a + b) >>> 0;
}

export function md5(input: string): string {
  const bytes = stringToBytes(input);
  const bitLength = bytes.length * 8;
  const paddedLength = (((bytes.length + 8) >> 6) + 1) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 8, bitLength >>> 0, true);
  view.setUint32(paddedLength - 4, Math.floor(bitLength / 0x100000000), true);

  let a0 = 0x67452301;
  let b0 = 0xefcdab89;
  let c0 = 0x98badcfe;
  let d0 = 0x10325476;

  const shifts = [
    7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
    5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
    4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
    6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
  ];
  const constants = Array.from({ length: 64 }, (_, i) =>
    Math.floor(Math.abs(Math.sin(i + 1)) * 0x100000000) >>> 0,
  );

  for (let offset = 0; offset < paddedLength; offset += 64) {
    const words = Array.from({ length: 16 }, (_, i) => view.getUint32(offset + i * 4, true));
    let a = a0;
    let b = b0;
    let c = c0;
    let d = d0;

    for (let i = 0; i < 64; i++) {
      let f: number;
      let g: number;
      if (i < 16) {
        f = (b & c) | (~b & d);
        g = i;
      } else if (i < 32) {
        f = (d & b) | (~d & c);
        g = (5 * i + 1) % 16;
      } else if (i < 48) {
        f = b ^ c ^ d;
        g = (3 * i + 5) % 16;
      } else {
        f = c ^ (b | ~d);
        g = (7 * i) % 16;
      }
      const next = d;
      d = c;
      c = b;
      b = add32(b, rotateLeft(add32(add32(a, f), add32(constants[i] ?? 0, words[g] ?? 0)), shifts[i] ?? 0));
      a = next;
    }

    a0 = add32(a0, a);
    b0 = add32(b0, b);
    c0 = add32(c0, c);
    d0 = add32(d0, d);
  }

  const out = new Uint8Array(16);
  const outView = new DataView(out.buffer);
  outView.setUint32(0, a0, true);
  outView.setUint32(4, b0, true);
  outView.setUint32(8, c0, true);
  outView.setUint32(12, d0, true);
  return bytesToHex(out);
}

const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4,
  0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe,
  0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f,
  0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc,
  0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116,
  0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
  0xc67178f2,
];

function rightRotate(value: number, shift: number): number {
  return (value >>> shift) | (value << (32 - shift));
}

function sha256Bytes(input: Uint8Array): Uint8Array {
  const bitLength = input.length * 8;
  const paddedLength = (((input.length + 8) >> 6) + 1) * 64;
  const padded = new Uint8Array(paddedLength);
  padded.set(input);
  padded[input.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(paddedLength - 4, bitLength >>> 0, false);
  view.setUint32(paddedLength - 8, Math.floor(bitLength / 0x100000000), false);

  const hash = [
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab,
    0x5be0cd19,
  ];
  const words = new Array<number>(64).fill(0);

  for (let offset = 0; offset < paddedLength; offset += 64) {
    for (let i = 0; i < 16; i++) words[i] = view.getUint32(offset + i * 4, false);
    for (let i = 16; i < 64; i++) {
      const s0 = rightRotate(words[i - 15] ?? 0, 7) ^ rightRotate(words[i - 15] ?? 0, 18) ^ ((words[i - 15] ?? 0) >>> 3);
      const s1 = rightRotate(words[i - 2] ?? 0, 17) ^ rightRotate(words[i - 2] ?? 0, 19) ^ ((words[i - 2] ?? 0) >>> 10);
      words[i] = add32(add32(add32(words[i - 16] ?? 0, s0), words[i - 7] ?? 0), s1);
    }

    let [a, b, c, d, e, f, g, h] = hash;
    for (let i = 0; i < 64; i++) {
      const s1 = rightRotate(e ?? 0, 6) ^ rightRotate(e ?? 0, 11) ^ rightRotate(e ?? 0, 25);
      const ch = ((e ?? 0) & (f ?? 0)) ^ (~(e ?? 0) & (g ?? 0));
      const temp1 = add32(add32(add32(add32(h ?? 0, s1), ch), SHA256_K[i] ?? 0), words[i] ?? 0);
      const s0 = rightRotate(a ?? 0, 2) ^ rightRotate(a ?? 0, 13) ^ rightRotate(a ?? 0, 22);
      const maj = ((a ?? 0) & (b ?? 0)) ^ ((a ?? 0) & (c ?? 0)) ^ ((b ?? 0) & (c ?? 0));
      const temp2 = add32(s0, maj);
      h = g;
      g = f;
      f = e;
      e = add32(d ?? 0, temp1);
      d = c;
      c = b;
      b = a;
      a = add32(temp1, temp2);
    }

    hash[0] = add32(hash[0] ?? 0, a ?? 0);
    hash[1] = add32(hash[1] ?? 0, b ?? 0);
    hash[2] = add32(hash[2] ?? 0, c ?? 0);
    hash[3] = add32(hash[3] ?? 0, d ?? 0);
    hash[4] = add32(hash[4] ?? 0, e ?? 0);
    hash[5] = add32(hash[5] ?? 0, f ?? 0);
    hash[6] = add32(hash[6] ?? 0, g ?? 0);
    hash[7] = add32(hash[7] ?? 0, h ?? 0);
  }

  const out = new Uint8Array(32);
  const outView = new DataView(out.buffer);
  hash.forEach((word, i) => outView.setUint32(i * 4, word, false));
  return out;
}

export function sha256Hex(input: string | Uint8Array): string {
  return bytesToHex(sha256Bytes(typeof input === 'string' ? stringToBytes(input) : input));
}

export function hmacString(algorithm: string, key: string, message: string): string {
  if (algorithm.toLowerCase() !== 'sha256') {
    throw new Error(`Unsupported HMAC algorithm: ${algorithm}`);
  }
  const blockSize = 64;
  let keyBytes = stringToBytes(key);
  if (keyBytes.length > blockSize) keyBytes = sha256Bytes(keyBytes);
  const paddedKey = new Uint8Array(blockSize);
  paddedKey.set(keyBytes);
  const outer = new Uint8Array(blockSize);
  const inner = new Uint8Array(blockSize);
  for (let i = 0; i < blockSize; i++) {
    outer[i] = (paddedKey[i] ?? 0) ^ 0x5c;
    inner[i] = (paddedKey[i] ?? 0) ^ 0x36;
  }
  const messageBytes = stringToBytes(message);
  const innerInput = new Uint8Array(inner.length + messageBytes.length);
  innerInput.set(inner);
  innerInput.set(messageBytes, inner.length);
  const innerHash = sha256Bytes(innerInput);
  const outerInput = new Uint8Array(outer.length + innerHash.length);
  outerInput.set(outer);
  outerInput.set(innerHash, outer.length);
  return bytesToHex(sha256Bytes(outerInput));
}
